import * as React from "react";
import { merge, $, media } from "glamor";
import IconButton from "material-ui/IconButton";
import Caret from "components/icons/caret";
import classNames from "classnames";
import { BrandDropdown } from "./brand-dropdown";
import { formatMessage } from "lib/globalize-util";
import { useMediaPredicate } from "react-media-hook";
import { DATATEST } from "constants/integration";
import { IFetchBrandsDataPayload } from "actions/brands-data-actions";
import { IBrandsDataStatePayload } from "reducers/brands-data-reducers";

interface IProps {
    router: any;
    location: any;
    brandIds: string[];
    brandsDataById?: IBrandsDataStatePayload["brandsDataById"];
    params: any;
    selectedBrand?: string;
    isFetchingBrands: boolean;
    me: any;
    onFetchBrandsData: (query: IFetchBrandsDataPayload["query"]) => void;
}

export const Scope: React.FunctionComponent<IProps> = ({
    router,
    location,
    brandIds,
    brandsDataById,
    params,
    me,
    selectedBrand,
    onFetchBrandsData,
    isFetchingBrands,
}) => {
    const styles = React.useMemo(() => {
        const TAB_WIDTH = 200;
        const TAB_WIDTH_TABLET = 140;
        const UNSELECTED_TEXT_COLOR = "rgba(255, 255, 255, 0.32)";
        const HOVER_TEXT_COLOR = "rgba(255, 255, 255, 0.8)";

        return merge(
            {
                position: "relative",
                height: "36px",
                padding: "4px",
                backgroundColor: "rgba(255, 255, 255, 0.16)",
                borderRadius: "36px",
                overflow: "hidden",
            },
            $(" ul", {
                position: "relative",
                display: "flex",
                margin: 0,
                zIndex: 2,
            }),
            $(" li", {
                position: "relative",
            }),
            $(" li a", {
                width: `${TAB_WIDTH}px`,
                textAlign: "center",
                height: "28px",
                lineHeight: "28px",
                color: UNSELECTED_TEXT_COLOR,
                fontSize: "14px",
                fontWeight: "bold",
                display: "block",
                transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
            }),
            $(" li:hover a", {
                color: HOVER_TEXT_COLOR,
            }),
            $(" li.selected a", {
                color: "white",
            }),
            $(" li.brandScopeContainer a", {
                paddingLeft: "8px",
                paddingRight: "26px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }),
            $(" .brandPickerButton", {
                position: "absolute !important",
                top: "-8px",
                right: "-4px",
                zIndex: 4,
            }),
            $(" .brandPickerButton svg", {
                fill: `${UNSELECTED_TEXT_COLOR} !important`,
            }),
            $(" li:hover .brandPickerButton svg", {
                fill: `${HOVER_TEXT_COLOR} !important`,
            }),
            $(" li.selected .brandPickerButton svg", {
                fill: "white !important",
            }),
            $(" .pill", {
                position: "absolute",
                top: "4px",
                left: "4px",
                zIndex: 1,
                background: UNSELECTED_TEXT_COLOR,
                height: "28px",
                width: `${TAB_WIDTH}px`,
                borderRadius: "28px",
                transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
            }),
            $(" .pill.position2", {
                transform: `translateX(${TAB_WIDTH}px)`,
            }),
            media(
                "(max-width: 1000px)",
                $(" .pill", {
                    width: `${TAB_WIDTH_TABLET}px`,
                }),
                $(" .pill.position2", {
                    transform: `translateX(${TAB_WIDTH_TABLET}px)`,
                    width: `${TAB_WIDTH_TABLET}px`,
                }),
                $(" li a", {
                    width: `${TAB_WIDTH_TABLET}px`,
                })
            ),
            media(
                "(max-width: 600px)",
                $(" .noScope", {
                    display: "none",
                }),
                $(" .pill", {
                    width: `${TAB_WIDTH_TABLET}px`,
                    opacity: 0,
                }),
                $(" .pill.position2", {
                    transform: "translateX(0px)",
                    width: `${TAB_WIDTH_TABLET}px`,
                    opacity: 1,
                })
            )
        );
    }, []);

    const isLessThan600px = useMediaPredicate("(max-width: 600px)");

    const locationRef = React.useRef(location);
    locationRef.current = location;

    const paramsRef = React.useRef(params);
    paramsRef.current = params;

    const brand = React.useMemo(() => {
        return brandsDataById[params.brand || me.last_brand];
    }, [params.brand, me.last_brand, brandsDataById]);

    const brandPopoverRef = React.useRef<HTMLDivElement>();

    const inBrandScope = React.useMemo<boolean>(() => {
        return !!params.brand;
    }, [location, params.brand]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateBrands = React.useCallback(
        (brandMap) => {
            // fetch all brands only when collections only have 2 brands or less
            if (Object.keys(brandMap).length <= 2) {
                // Call fast `onFetchBrandsData` to quickly fetch brands to show in brand-picker
                onFetchBrandsData({ assortment_brands_only: true });
            }
        },
        [onFetchBrandsData]
    );

    const [brandDialogOpen, setBrandDialogOpen] = React.useState(false);
    const handleOpenDialog = React.useCallback(() => {
        setBrandDialogOpen(true);
        updateBrands(brandsDataById);
    }, [brandsDataById, updateBrands, updateBrands]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleCloseDialog = React.useCallback(() => setBrandDialogOpen(false), []);

    const handleChangeScope = React.useCallback(
        (e) => {
            e.preventDefault();
            const searchParam = ""; // locationRef.current.pathname.includes("/search") ? "/search" : "";
            if (e.currentTarget.getAttribute("data-scope") === "brand") {
                const alreadyInBrandScope = !!paramsRef.current.brand;
                if (alreadyInBrandScope) {
                    setBrandDialogOpen(true);
                    updateBrands(brandsDataById);
                } else {
                    const query = {
                        ...locationRef.current.query,
                    };
                    delete query.filters;
                    // If in shop page and click on brand button
                    if (me.last_brand) {
                        router.push({
                            pathname: `/assortment/${paramsRef.current.id}/shop/brand/${me.last_brand}${searchParam}`,
                            query,
                        });
                    } else {
                        router.push({
                            pathname: `/assortment/${paramsRef.current.id}/shop`,
                            query,
                        });
                    }
                }
            } else {
                delete locationRef.current.query.filters;
                router.push({
                    pathname: paramsRef.current.brand
                        ? `/assortment/${paramsRef.current.id}/shop${searchParam}`
                        : `/assortment/${paramsRef.current.id}/shop`,
                    query: {
                        ...locationRef.current.query,
                    },
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [brandsDataById, updateBrands]
    );

    const handleBrandChange = React.useCallback((brandId) => {
        const query = {
            ...locationRef.current.query,
            // brand: brandId,
        };

        router.push({
            pathname: `/assortment/${paramsRef.current.id}/shop/brand/${brandId}`,
            query,
        });
        handleCloseDialog();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // hide picker when assortment is brand-scoped
    if (selectedBrand) {
        return null;
    }
    return (
        <div {...styles} ref={brandPopoverRef} className="scope">
            <ul>
                {!me.isBrandUser() && (
                    <li className={classNames("noScope", { selected: !inBrandScope })}>
                        <a
                            data-test={DATATEST.ASSORTMENTS.ALL_BRANDS_TOGGLE}
                            href="#"
                            data-scope="all"
                            onClick={handleChangeScope}
                        >
                            {formatMessage("All Brands")}
                        </a>
                    </li>
                )}
                <li className={classNames("brandScopeContainer", { selected: inBrandScope })}>
                    <a
                        data-test={DATATEST.ASSORTMENTS.BRAND_TOGGLE}
                        href="#"
                        data-scope={isLessThan600px && inBrandScope ? "all" : "brand"}
                        onClick={handleChangeScope}
                    >
                        {brand ? brand.name : "Select brand"}
                    </a>
                    <IconButton
                        data-test={DATATEST.ASSORTMENTS.BRAND_PICKER}
                        className="brandPickerButton"
                        touchRippleColor="white"
                        onClick={handleOpenDialog}
                    >
                        <Caret />
                    </IconButton>
                </li>
            </ul>
            <div className={classNames("pill", { position2: inBrandScope, hidden: me.isBrandUser() })} />
            <BrandDropdown
                ids={brandIds}
                entities={brandsDataById}
                anchorEl={brandPopoverRef}
                open={brandDialogOpen}
                onRequestClose={handleCloseDialog}
                onBrandChange={handleBrandChange}
                isFetchingBrands={isFetchingBrands}
            />
        </div>
    );
};
